
  import {mapSectionComponents} from '@/helper/SectionHelper';
  import {getMetaTags} from '@/helper/Seo';
  import LazyHydrate from 'vue-lazy-hydration';
  import {scopeHelper} from '@/helper/ScopeHelper';
  import startCase from 'lodash/startCase';
  import axios from 'axios';

  export default {
    name: 'ArticlesCategory',
    components: {
      LazyHydrate,
    },
    async asyncData({route, error, $cachedStrapi, redirect, app, store}) {
      // Initialize variables
      const filterParams = {
        search_term: null,
        categories: [],
      };

      // Query page from Strapi; strapi-change for findOne is required!
      const pagePromise = $cachedStrapi
        .findOne('pages', 'articles-category')
        .catch((e) => {
          if (e.statusCode !== 404) {
            throw e;
          }
        });

      // Query article-category from Strapi; strapi-change for findOne is required!
      const articleCategoryPromise = $cachedStrapi
        .findOne('article-categories', route.params.category)
        .catch(async () => {
          const slug = route.params.category;
          const readableSlug = startCase(slug.replace(/-/g, ' '));
          const currentQuery = route.query;
          const response = await axios.get(
            process.env.STRAPI_URL + '/article-categories',
            {
              params: {
                slug,
                _publicationState: 'preview',
              },
            }
          );
          if (response.data.length > 0) {
            const newQueryParam = {
              ...currentQuery,
              mtm_group: 'article-category-deactivated',
              search: readableSlug,
            };

            const redirectRoute = app.localePath({
              name: 'articles',
              query: newQueryParam,
            });

            // Redirect the user to the main listing page (with status code 302)
            redirect(301, redirectRoute);
          } else {
            // Query parameters for inactive jobs for appended
            const newQueryParam = {
              ...currentQuery,
              mtm_group: 'article-category-not-found',
              search: readableSlug,
            };

            const redirectRoute = app.localePath({
              name: 'articles',
              query: newQueryParam,
            });

            // Redirect the user to the main listing page (with status code 301)
            redirect(302, redirectRoute);
          }
          return;
        });

      const [page, articleCategory] = await Promise.all([
        pagePromise,
        articleCategoryPromise,
      ]);

      if (!scopeHelper(page, process.env.SCOPE_CANDIDATE)) {
        error({statusCode: 404, message: 'Page not found'});
        return;
      }
      if (!scopeHelper(articleCategory, process.env.SCOPE_CANDIDATE)) {
        error({statusCode: 404, message: 'Article not found'});
        return;
      }

      filterParams.categories = [articleCategory.slug];

      let search_url = '';
      if (store.getters['breadcrumbs/hasArticleListingRoute']) {
        const lastArticleListingRoute =
          store.state.breadcrumbs.lastArticleListingRoute;

        search_url =
          app.localePath({
            name: 'articles',
          }) +
          '?' +
          lastArticleListingRoute.split('?')[1];
      }

      const sections = await mapSectionComponents(
        page.sections,
        async function (section) {
          switch (section.component) {
            case 'SectionArticleListingWithFilter':
              section.filterParams = filterParams;
              section.content_scope = process.env.SCOPE_CANDIDATE;
              break;

            case 'SectionBreadcrumbs':
              section.breadcrumbs = [
                {
                  text: app.i18n.t('page.index'),
                  disabled: false,
                  to: app.localePath({
                    name: 'index',
                  }),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: app.i18n.t('page.articles'),
                  disabled: false,
                  to: store.getters['breadcrumbs/hasArticleListingRoute']
                    ? search_url
                    : app.localePath({
                        name: 'articles',
                      }),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: articleCategory.title,
                  disabled: true,
                  to: app.localePath({
                    name: 'articles-category',
                    params: {
                      category: articleCategory.slug,
                    },
                  }),
                  nuxt: true,
                  exact: true,
                },
              ];
              break;

            case 'SectionSeoContent':
              if (
                articleCategory.seo_content_config &&
                articleCategory.seo_content_config.seo_contents &&
                articleCategory.seo_content_config.seo_contents[0]
              ) {
                section.seoContent = await $cachedStrapi.findOne(
                  'seo-contents',
                  articleCategory.seo_content_config.seo_contents[0].id
                );
              } else if (
                section.seo_content_config &&
                section.seo_content_config.seo_contents &&
                section.seo_content_config.seo_contents[0]
              ) {
                section.seoContent = await $cachedStrapi.findOne(
                  'seo-contents',
                  section.seo_content_config.seo_contents[0].id
                );
              }
              break;
          }
        }
      );

      const metaData =
        articleCategory && articleCategory.meta
          ? articleCategory.meta
          : page && page.meta
          ? page.meta
          : {};

      return {
        metaData,
        articleCategory,
        filterParams,
        sections,
      };
    },
    data() {
      return {
        metaData: {},
        articleCategory: {},
        sections: [],

        filterParams: {
          search_term: null,
          categories: [],
        },
      };
    },
    head() {
      return {
        title: this.metaData.title,
        meta: getMetaTags(this.metaData),
      };
    },
    methods: {
      setFilterParams(params) {
        this.filterParams = params;
      },
      resetFilterParams() {
        this.filterParams = {};
        this.page = 1;
      },
    },
  };
